import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Input de password con toggle de visibilidad (ícono del "ojito").
 *
 * Drop-in replacement para `<input type="password">`. Acepta todas las
 * props del input nativo. Se diseñó para vivir DENTRO de un wrapper con
 * `position: relative` (ej. el div que ya contiene un ícono de Lock a
 * la izquierda) — el botón de toggle se posiciona absolutamente a la
 * derecha de ese wrapper.
 *
 * Usage:
 *   <div className="relative">
 *     <Lock className="absolute left-4 ..." />
 *     <PasswordInput
 *       className="w-full pl-11 pr-12 ..."  // pr-12 deja espacio al toggle
 *       value={password}
 *       onChange={(e) => setPassword(e.target.value)}
 *       placeholder="Tu contraseña"
 *     />
 *   </div>
 *
 * Notas de UX/accesibilidad:
 *   - `tabIndex={-1}` en el botón para que Tab vaya directo al submit
 *     después del campo, no al toggle (mejor flow de teclado).
 *   - aria-label dinámico según estado.
 *   - El botón usa min-h/w 32px para tap target cómodo en móvil.
 */
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const PasswordInput = forwardRef<HTMLInputElement, Props>(
  function PasswordInput({ className, ...rest }, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <>
        <input
          ref={ref}
          {...rest}
          type={visible ? 'text' : 'password'}
          className={className}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </>
    );
  },
);
